import { Injectable } from '@angular/core';
import { AbstractGitQueryEntity } from '@y42/feature/git/state';
import { CanvasElementType, CanvasFilterSettings, CanvasLayer, CanvasWidgetSettings } from '@y42/json-schema';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import { combineLatest, fromEvent, merge, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AvailableAllChartType } from '../../models/widget';
import { VisualizationFilterQuery, VisualizationFilterUIModel } from '../filter';
import { VisualizationTabStoreModel } from '../tab/visualization-tab.model';
import { VisualizationTabStoreQuery } from '../tab/visualization-tab.store-query';
import { VisualizationWidgetQuery, VisualizationWidgetUIModel } from '../widget';
import { layersZIndex } from './constants';
import { CanvasFilterUISettings, CanvasWidgetUISettings, isStickyItem, StickableItemType } from './models';
import { Point, Rect } from './types';
import { getItemsBoundaryRect } from './utils/boundaries.utils';
import { calculateBoundingRect } from './utils/size-and-position.utils';
import {
  VisualizationCanvasElementState,
  VisualizationCanvasElementStoreModel,
  VisualizationCanvasElementUIModel,
} from './visualization-canvas-element.model';
import { VisualizationCanvasElementStore } from './visualization-canvas-element.store';

export const WidgetInteractionKey = 'Alt';

export interface VisualizationCanvasElementGroup {
  groupId: string;
  items: VisualizationCanvasElementUIModel[];
}

@Injectable({ providedIn: 'root' })
export class VisualizationCanvasElementQuery extends AbstractGitQueryEntity<
  VisualizationCanvasElementState,
  VisualizationCanvasElementUIModel
> {
  constructor(
    protected readonly store: VisualizationCanvasElementStore,
    private readonly tabQuery: VisualizationTabStoreQuery,
    private readonly widgetQuery: VisualizationWidgetQuery,
    private readonly filterQuery: VisualizationFilterQuery,
  ) {
    super({
      store,
      toUIModel: (storeModel) => {
        const baseModel: VisualizationCanvasElementUIModel = {
          ...storeModel,
          tab: this.tabQuery.getEntity(storeModel.$parent as VisualizationTabStoreModel['$path'])!,
        };

        switch (baseModel.type) {
          case CanvasElementType.Widget:
            const widgetSettings = storeModel.typeSettings as CanvasWidgetSettings | null;
            const widget: VisualizationWidgetUIModel<AvailableAllChartType> | undefined = widgetSettings
              ? this.widgetQuery.getUIEntity(widgetSettings.widgetPath)
              : undefined;

            return {
              ...baseModel,
              typeSettings: { ...baseModel.typeSettings, widget } as CanvasWidgetUISettings,
            };

          case CanvasElementType.Filter:
            const filterSettings = storeModel.typeSettings as CanvasFilterSettings | null;
            const filterElement: VisualizationFilterUIModel | undefined = filterSettings
              ? this.filterQuery.getUIEntity(filterSettings.filterPath)
              : undefined;

            return {
              ...baseModel,
              typeSettings: { ...baseModel.typeSettings, filter: filterElement } as CanvasFilterUISettings,
            };

          default:
            return baseModel;
        }
      },
    });
  }

  readonly editMode$ = this.tabQuery.select().pipe(map((st) => st.ui.editMode));

  readonly active$: Observable<VisualizationCanvasElementUIModel | undefined> = this.selectActive().pipe(
    map((element) => (element ? this.toUIModel(element) : undefined)),
  );

  readonly items$ = combineLatest([this.tabQuery.selectActive(), this.selectAll(), this.widgetQuery.itemsByPath$]).pipe(
    map(([tab, elements]) =>
      tab
        ? elements
            .filter((element) => tab.$children?.includes(element.$path))
            .map((storeModel) => this.toUIModel(storeModel))
        : [],
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  ) as unknown as Observable<VisualizationCanvasElementUIModel[]>;

  readonly elementsByPath$ = this.itemsByPath$;

  readonly widgetInteractionKeyHeldDown$: Observable<boolean> = merge(
    fromEvent<KeyboardEvent>(document, 'keydown').pipe(
      filter((ev) => ev.key === WidgetInteractionKey),
      map(() => true),
    ),
    fromEvent<KeyboardEvent>(document, 'keyup').pipe(
      filter((ev) => ev.key === WidgetInteractionKey),
      map(() => false),
    ),
  ).pipe(startWith(false), distinctUntilChanged());

  readonly editModeWithInteractionKey$ = combineLatest([this.editMode$, this.widgetInteractionKeyHeldDown$]).pipe(
    map(([dashboardEditMode, widgetInteractionKeyHeldDown]) => dashboardEditMode && widgetInteractionKeyHeldDown),
    distinctUntilChanged(),
  );

  readonly selection$ = this.select((state) => state.ui.currentSelectionIds).pipe(
    switchMap((selectedIds) => this.items$.pipe(map((items) => items.filter((i) => selectedIds.includes(i.$path))))),
    map((items) => items.map((entity) => this.toUIModel(entity))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly independentItems$ = this.items$.pipe(
    map((items) => items.filter((entity) => !isStickyItem(entity) || !entity.typeSettings?.attachedTo)),
    map((items) => items.map((entity) => this.toUIModel(entity))),
  );

  readonly groupedItems$ = this.items$.pipe(
    map((items) => {
      const groups = groupBy(
        items.filter((item) => !!item.groupId),
        'groupId',
      );

      return Object.entries(groups).map(([groupId, groupItems]) => ({ groupId, items: groupItems }));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly extremeZOrders$: Observable<ExtremeZOrders> = this.items$.pipe(
    map(getExtremeZOrders),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
  );

  get items(): VisualizationCanvasElementUIModel[] {
    return this.getAll({
      filterBy: ({ $path: id }) => !!this.tabQuery.getActive()?.$children?.includes(id),
    }).map((item) => this.toUIModel(item));
  }

  get itemsMap() {
    return this.getAll({
      filterBy: ({ $path: id }) => !!this.tabQuery.getActive()?.$children?.includes(id),
      asObject: true,
    });
  }

  getUIState() {
    return this.select((state) => state.ui);
  }

  getUIValue() {
    return this.getValue().ui;
  }

  getSelection(): VisualizationCanvasElementUIModel[] {
    const selectedIds = this.getValue().ui.currentSelectionIds;
    return this.items.filter((entity) => selectedIds.includes(entity.$path)).map((item) => this.toUIModel(item));
  }

  getSelectionItemsIds(): string[] {
    return this.getValue().ui.currentSelectionIds;
  }

  getItemsBoundaryRect() {
    return getItemsBoundaryRect(this.items);
  }

  getItemAtPosition(
    position: Point,
    shouldIncludeItem: (item: VisualizationCanvasElementUIModel) => boolean = () => true,
  ): VisualizationCanvasElementUIModel | undefined {
    return this.items
      .filter(shouldIncludeItem)
      .filter((item) => isPositionInItem(position, item))
      .sort((item1, item2) => item2.zIndex + layersZIndex[item2.layer] - (item1.zIndex + layersZIndex[item1.layer]))
      .at(0);
  }

  getItemsAtRect(rect: Rect): VisualizationCanvasElementUIModel[] {
    return this.items.filter((item) => isItemInRect(item, rect));
  }

  selectStickyItems(): Observable<Array<VisualizationCanvasElementUIModel<StickableItemType>>> {
    return this.items$.pipe(
      map((items) => items.filter(isStickyItem) as Array<VisualizationCanvasElementUIModel<StickableItemType>>),
    );
  }
}

function isPositionInItem(position: Point, item: VisualizationCanvasElementStoreModel): boolean {
  // TODO: A naive approach with wrapping items in a bounding rect will fail for rotated items.
  const itemRect = calculateBoundingRect(item);

  return (
    position.x >= itemRect.x &&
    position.x <= itemRect.x + itemRect.width &&
    position.y >= itemRect.y &&
    position.y <= itemRect.y + itemRect.height
  );
}

function isItemInRect(item: VisualizationCanvasElementStoreModel, rect: Rect): boolean {
  // TODO: A naive approach with wrapping items in a bounding rect will fail for rotated items.
  const itemRect = calculateBoundingRect(item);

  return (
    rect.x <= itemRect.x &&
    rect.y <= itemRect.y &&
    rect.x + rect.width >= itemRect.x + itemRect.width &&
    rect.y + rect.height >= itemRect.y + itemRect.height
  );
}

export type ExtremeZOrders = Record<CanvasLayer, { highest: number; lowest: number }>;

function getExtremeZOrders(items: VisualizationCanvasElementUIModel[]) {
  items = items.filter((item) => !isNaN(item.zIndex));

  const layerToOrdersEntries = Object.entries(groupBy(items, (item) => item.layer)).map(
    ([layer, layerItems]) =>
      [
        layer as CanvasLayer,
        {
          highest: Math.max(...layerItems.map((item) => item.zIndex)),
          lowest: Math.min(...layerItems.map((item) => item.zIndex)),
        },
      ] as const,
  );

  // TS loses types of keys in the Object.fromEntries() call.
  return Object.fromEntries(layerToOrdersEntries) as ExtremeZOrders;
}
